import { Form, Formik } from 'formik';
import { trim } from 'lodash';
import PropTypes from 'prop-types';
import React, { useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';

import { AuthContext } from '@dashboard/context/AuthContext';

import { formatPriceInput } from '@dashboard/services/formatting';

import { Box } from '@dashboard/components/Box';
import Card from '@dashboard/components/Card';
import CardContent from '@dashboard/components/CardContent';
import CardHeader from '@dashboard/components/CardHeader';
import CardHeaderTitle from '@dashboard/components/CardHeaderTitle';
import CheckboxReveal from '@dashboard/components/CheckboxReveal';
import { FormElement, Label, LabelDescription } from '@dashboard/components/Form';
import { Checkbox, Select, TextInput } from '@dashboard/components/Formik';
import CurrencySelector from '@dashboard/components/Formik/components/CurrencySelector';
import FormError, { FormErrorBase } from '@dashboard/components/Formik/components/FormError';
import FormSubmit from '@dashboard/components/Formik/components/FormSubmit';
import PageMultiSelect from '@dashboard/components/Formik/components/PageMultiSelect';
import Radio from '@dashboard/components/Formik/components/Radio';
import { InfoTooltip } from '@dashboard/components/Tooltip';

import { saveCoupon } from './helpers';

const DiscountsCouponsCreate = () => {
  const { token, sellerId } = useContext(AuthContext);
  const [applyToSpecificPages, setApplyToSpecificPages] = useState(false);
  const history = useHistory();

  return (
    <Card variant="root">
      <CardHeader variant="root">
        <CardHeaderTitle variant="root">Create a coupon</CardHeaderTitle>
      </CardHeader>

      <CardContent variant="root" className="max-w-lg sm:mx-auto mt-4">
        <Formik
          validateOnBlur={false}
          validateOnChange={false}
          validationSchema={Yup.object().shape({
            label: Yup.string().required(),
            code: Yup.string().required(),
            discountType: Yup.string().required(),
            duration: Yup.string().required(),
            durationInMonths: Yup.number().when('duration', {
              is: duration => duration === 'repeating',
              then: Yup.number().required(),
            }),
            appliesToSetupFee: Yup.boolean(),
            amountOff: Yup.number()
              .nullable()
              .when('discountType', {
                is: 'amountOff',
                then: Yup.number().typeError('Amount off should be a number').required(),
              }),
            percentOff: Yup.number()
              .nullable()
              .when('discountType', {
                is: 'percentOff',
                then: Yup.number()
                  .min(1, 'Percentage off must be more than 0')
                  .max(100, 'Percentage off must be less than or equal to 100')
                  .typeError('Percentage off should be a number betwen 1 and 100')
                  .required(),
              }),
            currency: Yup.string().when('discountType', {
              is: 'amountOff',
              then: Yup.string().required(),
            }),
          })}
          initialValues={{
            label: '',
            code: '',
            pages: null,
            discountType: 'amountOff',
            duration: 'forever',
            durationInMonths: 1,
            amountOff: null,
            percentOff: null,
            currency: 'USD',
            appliesToSetupFee: false,
            redeemByDate: null,
            redeemByTime: null,
            maxRedemptions: null,
          }}
          onSubmit={async (values, { setSubmitting, setStatus, resetForm }) => {
            setStatus(undefined);

            try {
              const body = {
                ...values,
                code: trim(values.code),
                pages: applyToSpecificPages ? values.pages : null,
                amountOff:
                  values.discountType === 'amountOff'
                    ? formatPriceInput(values.amountOff, values.currency)
                    : null,
                percentOff: values.discountType === 'percentOff' ? Number(values.percentOff) : null,
              };

              if (body.redeemByDate) {
                body.redeemBy = new Date(`${body.redeemByDate} ${body.redeemByTime}`);
              }

              const response = await saveCoupon(body, sellerId, token);

              if (!response.data) {
                throw response;
              }

              history.push(`/coupons/${response.data._id}`);

              setStatus('success');
              resetForm({ values: response.data });
            } catch (err) {
              setStatus({ error: err.message || 'Something went wrong' });
            }

            setSubmitting(false);
          }}
        >
          {({ values, status, setFieldValue, handleSubmit, setFieldTouched, setFieldError }) => {
            return (
              <Form onSubmit={handleSubmit}>
                <FormElement>
                  <Label htmlFor="label">Label</Label>
                  <TextInput
                    name="label"
                    id="label"
                    type="text"
                    placeholder="Coupon label"
                    required
                    border
                    autoFocus
                  />
                  <FormError name="label" />
                </FormElement>

                <FormElement>
                  <Label
                    htmlFor="code"
                    description="This is the code you share with your customers"
                  >
                    Code
                  </Label>
                  <TextInput
                    name="code"
                    id="code"
                    type="text"
                    placeholder="Coupon code"
                    required
                    border
                  />
                  <FormError name="code" />
                </FormElement>

                <FormElement>
                  <Label htmlFor="discountType">Discount type</Label>
                  <Radio.Group name="discountType" id="discountType">
                    <Label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Radio.Input
                        name="discountType"
                        value="amountOff"
                        checked={values.discountType === 'amountOff'}
                        onChange={() => {
                          setFieldValue('discountType', 'amountOff');
                          setFieldValue('percentOff', '');
                          setFieldTouched('percentOff', false, false);
                          setFieldError('percentOff', '');
                        }}
                        required
                      />
                      <span
                        style={{
                          marginLeft: '.5em',
                          verticalAlign: 'middle',
                        }}
                      >
                        Fixed amount
                      </span>
                    </Label>
                    <Label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Radio.Input
                        name="discountType"
                        value="percentOff"
                        checked={values.discountType === 'percentOff'}
                        onChange={() => {
                          setFieldValue('discountType', 'percentOff');
                          setFieldValue('amountOff', '');
                          setFieldTouched('amountOff', false, false);
                          setFieldError('amountOff', '');
                        }}
                        required
                      />
                      <span
                        style={{
                          marginLeft: '.5em',
                          verticalAlign: 'middle',
                        }}
                      >
                        Percentage
                      </span>
                    </Label>
                  </Radio.Group>
                  <FormError name="discountType" />
                </FormElement>

                {values.discountType === 'amountOff' && (
                  <FormElement>
                    <Box className="gap-4">
                      <div className="w-36">
                        <Label htmlFor="amountOff">Amount off</Label>
                        <TextInput
                          name="amountOff"
                          id="amountOff"
                          type="text"
                          placeholder="Amount off"
                          required={values.discountType === 'amountOff'}
                          border
                        />
                      </div>
                      <div>
                        <Label htmlFor="currency">Currency</Label>
                        <CurrencySelector name="currency" />
                      </div>
                    </Box>
                    <FormError name="amountOff" />
                    <FormError name="currency" />
                  </FormElement>
                )}

                {values.discountType === 'percentOff' && (
                  <FormElement>
                    <Box>
                      <div className="w-36">
                        <Label htmlFor="percentOff">Percentage off</Label>
                        <TextInput
                          name="percentOff"
                          id="percentOff"
                          type="text"
                          placeholder="Percentage off"
                          required={values.discountType === 'percentOff'}
                          border
                        />
                      </div>
                    </Box>
                    <FormError name="percentOff" />
                  </FormElement>
                )}

                <FormElement>
                  <Checkbox.Wrapper>
                    <Checkbox id="appliesToSetupFee" name="appliesToSetupFee" />
                    <Checkbox.Label htmlFor="appliesToSetupFee">
                      Apply discount to setup fee
                    </Checkbox.Label>
                  </Checkbox.Wrapper>
                </FormElement>

                <FormElement>
                  <Label description="For subscriptions, choose a duration for which the discount will be applied.">
                    Coupon duration
                  </Label>
                  <FormElement className="flex-1 max-w-lg mr-4">
                    <Select id="duration" name="duration" required>
                      <option value="forever" selected>
                        Every payment
                      </option>
                      <option value="once">First payment</option>
                      <option value="repeating">Multiple payments</option>
                    </Select>
                  </FormElement>
                  <FormError name="duration" />
                </FormElement>

                {values.duration === 'repeating' && (
                  <FormElement className="w-1/3">
                    <Label htmlFor="durationInMonths">
                      Duration in months{' '}
                      <InfoTooltip>
                        The number of months the discount code will be applied for.
                      </InfoTooltip>
                    </Label>
                    <TextInput type="number" id="durationInMonths" name="durationInMonths" />
                    <FormError name="durationInMonths" />
                  </FormElement>
                )}

                <h3 className="text-lg font-bold pb-4 text-gray-900">Redemption limits</h3>

                <FormElement className="space-y-2">
                  <CheckboxReveal
                    label="Number of times this coupon can be redeemed"
                    onToggle={() => setFieldValue('maxRedemptions', null)}
                  >
                    {({ visible }) => {
                      if (!visible) return null;

                      return (
                        <div className="ml-6">
                          <TextInput
                            style={{ maxWidth: '200px' }}
                            type="number"
                            name="maxRedemptions"
                            id="maxRedemptions"
                            placeholder="10"
                            border
                          />
                          <LabelDescription className="mt-2 mb-4">
                            {`The total amount of times this coupon can be used.
                            Doesn't limit a single customer from redeeming
                            multiple times.`}
                          </LabelDescription>

                          <FormError name="maxRedemptions" />
                        </div>
                      );
                    }}
                  </CheckboxReveal>

                  <CheckboxReveal
                    label="Redeem by date & time"
                    onToggle={() => {
                      setFieldValue('redeemByDate', null);
                      setFieldValue('redeemByTime', null);
                    }}
                  >
                    {({ visible }) => {
                      if (!visible) return null;

                      return (
                        <div className="ml-6 pb-4">
                          <Box>
                            <TextInput
                              style={{
                                maxWidth: '200px',
                                marginRight: '0.5em',
                              }}
                              type="date"
                              name="redeemByDate"
                              id="redeemByDate"
                              placeholder="YYYY-MM-DD"
                              border
                            />
                            <TextInput
                              style={{ maxWidth: '200px' }}
                              type="time"
                              name="redeemByTime"
                              id="redeemByTime"
                              placeholder="hh:mm"
                              border
                            />
                          </Box>
                          <FormError name="redeemByDate" />
                          <FormError name="redeemByTime" />
                        </div>
                      );
                    }}
                  </CheckboxReveal>

                  <CheckboxReveal
                    label="Apply coupon to specific pages"
                    onToggle={visible => setApplyToSpecificPages(visible)}
                  >
                    {({ visible }) => {
                      return (
                        <div
                          className="ml-6 pb-4"
                          style={{
                            display: visible ? 'block' : 'none',
                          }}
                        >
                          <PageMultiSelect
                            onChange={values => setFieldValue('pages', values)}
                            pageType={['checkout', 'event']}
                          />

                          <FormError name="pages" />
                        </div>
                      );
                    }}
                  </CheckboxReveal>
                </FormElement>

                <FormErrorBase>{status && status.error && status.error}</FormErrorBase>

                <FormSubmit>
                  <FormSubmit.Button appearance="primary">Create coupon</FormSubmit.Button>
                </FormSubmit>
              </Form>
            );
          }}
        </Formik>
      </CardContent>
    </Card>
  );
};

DiscountsCouponsCreate.propTypes = {
  history: PropTypes.object.isRequired,
};

export default DiscountsCouponsCreate;
